import React, { useEffect, useState, useCallback, useContext } from 'react';
import './object.css';
import { validateSteps } from "../validateSteps";
import axios from 'axios';
import { useLoadScript, GoogleMap, Marker } from '@react-google-maps/api';
import { LanguageContext } from "../context/LanguageContext";

const containerStyle = {
  width: '100%',
  height: '300px'
};

const defaultCenter = {
  lat: 46.8,
  lng: 8.2
};

const MapComponent = ({ markerPosition, setMarkerPosition, zoom, setManualMarker, texts }) => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  });

  const onMapClick = (e) => {
    const newPos = { lat: e.latLng.lat(), lng: e.latLng.lng() };
    setMarkerPosition(newPos);
    setManualMarker(true);
  };

if (loadError) return <div>{texts.object.step1.map.load_error}</div>;
if (!isLoaded) return <div>{texts.object.step1.map.loading}</div>;



  return (
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={markerPosition || defaultCenter}
      zoom={zoom}
      onClick={onMapClick}
    >
      <Marker
        position={markerPosition || defaultCenter}
        draggable={true}
        onDragEnd={(e) => {
          const newPos = { lat: e.latLng.lat(), lng: e.latLng.lng() };
          setMarkerPosition(newPos);
          setManualMarker(true);
        }}
      />
    </GoogleMap>
  );
};

const CreateObjectStep1 = ({
  formData,
  setFormData,
  onNext,
  missingFields = [],
  setMissingFields,
  validationActive,
  setValidationActive
}) => {

  const { language, texts } = useContext(LanguageContext);
  const [countries, setCountries] = useState([]);
  const [regions, setRegions] = useState([]);
  const [filteredRegions, setFilteredRegions] = useState([]);
  const [objectTypes, setObjectTypes] = useState([]);
  const [licenceRequired, setLicenceRequired] = useState(false);

  const [markerPosition, setMarkerPosition] = useState(
    formData.latitude && formData.longitude
      ? { lat: parseFloat(formData.latitude), lng: parseFloat(formData.longitude) }
      : defaultCenter
  );

  const [mapZoom, setMapZoom] = useState(17);

  const updateManualFlag = (value) => {
    setFormData(prev => ({
      ...prev,
      markerManuallySet: value
    }));
  };

  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      latitude: markerPosition.lat,
      longitude: markerPosition.lng,
    }));
  }, [markerPosition, setFormData]);

    useEffect(() => {
      const fetchDropdownData = async () => {
        try {
          const [countriesRes, regionsRes, objectTypesRes] = await Promise.all([
            axios.get(`${process.env.REACT_APP_API_URL}/api/countries?lang=${language}`),
            axios.get(`${process.env.REACT_APP_API_URL}/api/regions?lang=${language}`),
            axios.get(`${process.env.REACT_APP_API_URL}/api/object-types?lang=${language}`)
          ]);
          setCountries(countriesRes.data);
          setRegions(regionsRes.data);
          setObjectTypes(objectTypesRes.data);
        } catch (error) {
          console.error('Fehler beim Laden der Dropdown-Daten:', error);
        }
      };
      fetchDropdownData();
    }, [language]);


  useEffect(() => {
    const countryIdNum = formData.country_id ? parseInt(String(formData.country_id), 10) : null;
    const filtered = regions.filter(r => r.country_id === countryIdNum);
    setFilteredRegions(filtered);

    setFormData(prev => {
      const prevRegion = prev.region ? String(prev.region) : '';
      const prevRegionStillValid = prevRegion && filtered.some(fr => String(fr.id) === prevRegion);
      const countryChanged = prev.country_id !== formData.country_id;

      let newRegion;
      if (countryChanged) {
        newRegion = filtered.length > 0 ? '' : prevRegion;
      } else {
        newRegion = filtered.length > 0 ? (prevRegionStillValid ? prevRegion : '') : prevRegion;
      }

      return {
        ...prev,
        country_has_regions: filtered.length > 0,
        region: newRegion
      };
    });
  }, [formData.country_id, regions, setFormData]);

  // 🔥 Lizenzpflicht prüfen
  useEffect(() => {
    if (!formData.country_id) return;

    fetch(
      `${process.env.REACT_APP_API_URL}/api/licence_required?country_id=${formData.country_id}&region_id=${formData.region || ""}`
    )
      .then(res => res.json())
      .then(data => setLicenceRequired(data.required))
      .catch(err => console.error(err));
  }, [formData.country_id, formData.region]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    let updated;

    if (name === 'country_id') {
      updated = { ...formData, [name]: String(value), region: '' };
    } else {
      updated = { ...formData, [name]: value };
    }

    setFormData(updated);

    if (['street', 'house_number', 'postal_code', 'city'].includes(name)) {
      updateManualFlag(false);
    }

    if (validationActive) {
      const { missing } = validateSteps(updated, licenceRequired);
      setMissingFields(missing);
    }
  };

  useEffect(() => {
  if (validationActive) {
    const { missing } = validateSteps(formData, licenceRequired);
    setMissingFields(missing);
  }
}, [validationActive, licenceRequired]);

  const handleSetMarker = useCallback(() => {
    const { street, house_number, postal_code, city } = formData;
    if (street && house_number && postal_code && city && window.google && window.google.maps) {
      const geocoder = new window.google.maps.Geocoder();
      const fullAddress = `${street} ${house_number}, ${postal_code} ${city}`;
      geocoder.geocode({ address: fullAddress }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const location = results[0].geometry.location;
          setMarkerPosition({ lat: location.lat(), lng: location.lng() });
          setMapZoom(17);
          setFormData(prev => ({ ...prev, lastAutoAddress: fullAddress }));
          updateManualFlag(false);
        } else {
          console.error(texts.object.step1.geocode.failed, status);
        }
      });
    } else {
      console.error(texts.object.step1.geocode.missing_fields);
    }
  }, [formData, setMarkerPosition, setFormData]);

  useEffect(() => {
    if (formData.street && formData.house_number && formData.postal_code && formData.city) {
      const currentAddress = `${formData.street} ${formData.house_number}, ${formData.postal_code} ${formData.city}`;
      if (!formData.markerManuallySet && currentAddress !== formData.lastAutoAddress) {
        handleSetMarker();
      }
    }
  }, [
    formData.street,
    formData.house_number,
    formData.postal_code,
    formData.city,
    formData.markerManuallySet,
    formData.lastAutoAddress,
    handleSetMarker
  ]);

return (
  <div className="object-form-container">
    <h2 className="object-header">{texts.object.step1.title}</h2>

    <div className="object-content-scroll-area">
      <div className="form-cards">

        <form className="object-form">
          <div className="object-form-layout" style={{ display: 'flex', gap: '20px' }}>

            {/* Linke Spalte */}
            <div className="object-form-fields" style={{ flex: 1 }}>
              {[
                { label: texts.object.step1.fields.name, name: 'name', type: 'text' },
                { label: texts.object.step1.fields.street, name: 'street', type: 'text' },
                { label: texts.object.step1.fields.house_number, name: 'house_number', type: 'text' },
                { label: texts.object.step1.fields.postal_code, name: 'postal_code', type: 'text' },
                { label: texts.object.step1.fields.city, name: 'city', type: 'text' }
              ].map(({ label, name, type }) => (
                <div className="object-form-group" key={name}>
                  <label htmlFor={name} className="object-form-label">{label}</label>
                  <input
                    type={type}
                    id={name}
                    name={name}
                    value={formData[name] || ''}
                    onChange={handleChange}
                    className={`object-form-input ${
                      validationActive && missingFields.includes(name) ? "input-error" : ""
                    }`}
                  />
                </div>
              ))}

              {/* Objektart */}
              <div className="object-form-group">
                <label htmlFor="object_type" className="object-form-label">
                  {texts.object.step1.fields.object_type}
                </label>

                <select
                  id="object_type"
                  name="object_type"
                  value={formData.object_type || ""}
                  onChange={handleChange}
                  className={`object-form-input ${
                    validationActive && missingFields.includes("object_type") ? "input-error" : ""
                  }`}
                >
                  <option value="">{texts.object.step1.select.choose}</option>

                  {objectTypes.map(obj => (
                    <option key={obj.id} value={obj.id}>
                      {obj.name}
                    </option>
                  ))}
                </select>
              </div>


              {/* Land */}
              <div className="object-form-group">
                <label htmlFor="country_id" className="object-form-label">
                  {texts.object.step1.fields.country}
                </label>

                <select
                  id="country_id"
                  name="country_id"
                  value={formData.country_id || ""}
                  onChange={handleChange}
                  className={`object-form-input ${
                    validationActive && missingFields.includes("country_id") ? "input-error" : ""
                  }`}
                >
                  <option value="">{texts.object.step1.select.choose}</option>

                  {countries.map(country => (
                    <option key={country.id} value={String(country.id)}>
                      {country.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Region */}
              {filteredRegions.length > 0 && (
                <div className="object-form-group">
                  <label htmlFor="region" className="object-form-label">
                    {texts.object.step1.fields.region}
                  </label>

                  <select
                    id="region"
                    name="region"
                    value={formData.region || ""}
                    onChange={handleChange}
                    className={`object-form-input ${
                      validationActive && missingFields.includes("region") ? "input-error" : ""
                    }`}
                  >
                    <option value="">{texts.object.step1.select.choose}</option>

                    {filteredRegions.map(region => (
                      <option key={region.id} value={String(region.id)}>
                        {region.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}


              {/* Lizenznummer */}
              {licenceRequired && (
                <div className="object-form-group">
                  <label htmlFor="licence_number">
                    {texts.object.step1.fields.licence_number}
                  </label>
                  <input
                    type="text"
                    id="licence_number"
                    name="licence_number"
                    value={formData.licence_number || ""}
                    onChange={handleChange}
                    className={`object-form-input ${
                      validationActive && missingFields.includes("licence_number") ? "input-error" : ""
                    }`}
                  />
                </div>
              )}
            </div>

            {/* Rechte Spalte */}
            <div className="object-map-column" style={{ flex: 1 }}>
              <h3 className="object-text">
                {texts.object.step1.map.info}
              </h3>

              <button
                type="button"
                className="object-button"
                onClick={handleSetMarker}
                style={{ marginBottom: '10px', backgroundColor: '#d4946f' }}
              >
                {texts.object.step1.map.reset}
              </button>

              <MapComponent
                markerPosition={markerPosition}
                setMarkerPosition={setMarkerPosition}
                zoom={mapZoom}
                setManualMarker={updateManualFlag}
                texts={texts}
              />

            </div>

          </div>
        </form>

      </div>
    </div>
  </div>
);


};

export default CreateObjectStep1;